import { HOMEWORK_PRACTICE_TITLE, LEGACY_HOMEWORK_PRACTICE_TITLE, HOMEWORK_PRACTICE_CONTENT, shouldUpgradeHomeworkPracticeTemplate, isBuiltInHomePracticeTemplate } from './homeworkPracticeTemplate';

// Read again on use: another tab may have edited, renamed or removed the template.
export async function prepareHomePracticeTemplate(supabase, user) {
  if (!user?.id) throw new Error('Sign in before preparing Home practice.');
  const { data: rows, error: readError } = await supabase.from('templates')
    .select('*').eq('userid', user.id)
    .order('updated_at', { ascending: false, nullsFirst: false }).order('id', { ascending: true });
  if (readError) throw readError;
  if (!Array.isArray(rows)) throw new Error('Could not read templates.');
  const candidates = await Promise.all(rows.map(async row => {
    const { data: title, error } = await supabase.rpc('decrypt_text', { encrypted_data: row.title });
    if (error || typeof title !== 'string') throw error || new Error('Could not read template title.');
    return { row, title };
  }));
  const found = candidates.find(item => item.title === HOMEWORK_PRACTICE_TITLE)
    || candidates.find(item => item.title === LEGACY_HOMEWORK_PRACTICE_TITLE);
  if (found) {
    const [content, createdBy] = await Promise.all([
      supabase.rpc('decrypt_text', { encrypted_data: found.row.content }),
      supabase.rpc('decrypt_text', { encrypted_data: found.row.created_by }),
    ]);
    if (content.error || typeof content.data !== 'string') throw content.error || new Error('Could not read template content.');
    if (createdBy.error) throw createdBy.error;
    const template = { ...found.row, title: found.title, content: content.data, created_by: createdBy.data };
    const updates = {};
    if (!template.is_base_template && isBuiltInHomePracticeTemplate(template)) updates.is_base_template = true;
    if (template.title === LEGACY_HOMEWORK_PRACTICE_TITLE) updates.title = HOMEWORK_PRACTICE_TITLE;
    if (shouldUpgradeHomeworkPracticeTemplate(template)) updates.content = HOMEWORK_PRACTICE_CONTENT;
    if (!Object.keys(updates).length) return template;

    // Keep long ciphertexts in the POST body. The database compares under a row
    // lock, preserving the stale-edit protection without putting content in URLs.
    const { data: status, error } = await supabase.rpc('prepare_home_practice_template', {
      p_template_id: template.id,
      p_original_title: found.row.title,
      p_original_content: found.row.content,
      p_original_is_base_template: found.row.is_base_template ?? null,
      p_changes: updates,
    });
    if (error) throw new Error('The template could not be saved. Please try again.');
    if (!['saved', 'already_saved'].includes(status)) throw new Error('The template changed or could not be saved. Please try again.');
    return { ...template, ...updates };
  }

  const { data, error } = await supabase.from('templates').insert([{
    title: HOMEWORK_PRACTICE_TITLE, content: HOMEWORK_PRACTICE_CONTENT,
    userid: user.id, created_by: user.name || 'Unknown User', is_base_template: true,
  }]).select().single();
  if (error) throw error;
  if (!data?.id) throw new Error('The template could not be saved.');
  return { ...data, is_base_template: true, title: HOMEWORK_PRACTICE_TITLE, content: HOMEWORK_PRACTICE_CONTENT, created_by: user.name || 'Unknown User' };
}
